// ignoring this for the entire file because dnd requires lots of props to be spread
import styled from '@emotion/styled';
import { Draggable } from '@hello-pangea/dnd';
import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, MouseEvent, PropsWithChildren, ReactNode } from 'react';
import { Fragment, memo, useCallback, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { FieldErrors } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileSkill, SkillProficiencyLevel } from '~/shared/data-contract/skill';
import { Badge, type BadgeProps } from '~/ui/core/badge/Badge';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from '~/ui/core/context-menu/ContextMenu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconChatBubble7, IconCopy } from '~/ui/core/icons';
import { ProficiencyScale } from '~/ui/domain/skill/ProficiencyScale';
import { SkillBadge } from '~/ui/domain/skill/SkillBadge';

import { useSkillProficiencyLevelName } from '../../../app/common/skills';
import { ANCHOR_SKILL_ID_ATTRIBUTE } from '../../../comments/anchor/grammar';
import { isRowActivationClick } from '../../../comments/anchor/row-activation';
import { useCommentableAnchor } from '../../../comments/anchor/use-commentable-anchor';
import { commentHighlightRowClassName } from '../../../comments/comment-highlight';
import { useCopyTextToClipboard } from '../../../comments/use-copy-text-to-clipboard';
import { SKILL_DETAIL_TILE_SLOT_KEY, selectSkillIdFromTile } from '../../../skills';
import { useOpenTileSelection } from '../../../url-tiles';
import { SkillNotSyncedWarning } from '../../components/SkillNotSyncedWarning';
import { useActiveLanguage } from '../../providers/ActiveLanguageProvider';
import type { ProfileForm } from '../types';
import { ProficiencyLevelDropdownMenu } from './ProficiencyLevelDropdownMenu';
import { SkillContextMenu } from './SkillContextMenu';
import type { JobProfileSkillWithDiff, SkillDiff } from './use-skills-with-compentencies';

const SKILL_DIFF_BADGE_VARIANT = {
    added: 'success',
    changed: 'warning',
    removed: 'error',
} as const satisfies Record<SkillDiff, BadgeProps['variant']>;

const SkillRow = styled.div(({ theme }) => ({
    '&[data-state="open"] [role="button"]': {
        backgroundColor: 'var(--color-surface-interactive)',
    },
    '& > *': {
        position: 'relative',
        zIndex: 1,
    },
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    ...theme.typography.body.S.medium,
    flexDirection: 'row',
    gap: theme.spacing.l,
    isolation: 'isolate',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing.s,
    paddingTop: theme.spacing.s,
    position: 'relative',
    width: '100%',
}));

const ProficiencyLevel = styled.div<{ readonly hasError?: boolean }>(({ hasError, theme }) => ({
    alignItems: 'center',
    boxShadow: hasError === true ? '0 0 0 1px var(--color-status-error-border)' : 'none',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.m,
    justifyContent: 'center',
    padding: theme.spacing.xs,
}));

const DragWrapper = styled.div<{ readonly isDragEnabled: boolean }>(({ isDragEnabled }) => ({
    '& > *': {
        cursor: isDragEnabled ? 'grab' : 'default',
    },
    '&:focus': {
        outline: 'none',
    },
    cursor: isDragEnabled ? 'grab' : 'default',
    outline: 'none',
    width: '100%',
}));

const Row = styled.div(({ theme }) => ({
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'row',
    gap: theme.spacing.s,
    justifyContent: 'flex-end',
    position: 'relative',
}));

const SkillBadgeSlot = styled.div({
    display: 'flex',
    flex: '1 1 auto',
    minWidth: 0,
});

const UnstyledButton = styled.button({
    '&:focus-visible': {
        outline: '1px solid',
        outlineColor: 'var(--color-focus-ring)',
    },
    all: 'unset',
});

type SkillRowContextMenuProps = PropsWithChildren<{
    readonly dataAttributes?: Record<string, string>;
    readonly highlightClassName?: string;
    readonly onComment?: (anchor: HTMLElement | null) => void;
    readonly onOpenSkillDetails: () => void;
    readonly onSelectExistingComment?: () => void;
    readonly skillId: string;
    readonly skillTitle: string;
}>;

const SkillRowContextMenu: FunctionComponent<SkillRowContextMenuProps> = ({
    children,
    dataAttributes,
    highlightClassName,
    onComment,
    onOpenSkillDetails,
    onSelectExistingComment,
    skillTitle,
}) => {
    const translate = useTranslate();
    const copyTextToClipboard = useCopyTextToClipboard();
    const rowRef = useRef<HTMLDivElement>(null);

    const handleCopy = async (): Promise<void> => {
        await copyTextToClipboard(skillTitle);
    };

    const handleRowClick = (event: MouseEvent<HTMLDivElement>): void => {
        if (!is.undefined(onSelectExistingComment) && isRowActivationClick(event)) onSelectExistingComment();
    };

    return (
        <ContextMenu>
            <ContextMenuTrigger>
                <SkillRow
                    ref={rowRef}
                    className={clsx('group', highlightClassName)}
                    data-testid="skill-row"
                    onClick={handleRowClick}
                    {...dataAttributes}
                >
                    {children}
                </SkillRow>
            </ContextMenuTrigger>
            <ContextMenuContent className="!min-w-[7rem]" onCloseAutoFocus={(event) => event.preventDefault()}>
                <ContextMenuItem onSelect={onOpenSkillDetails}>
                    <span aria-hidden={true} className="h-xl w-xl flex-none" />
                    {translate('pages.job-architecture-v2.open-skill')}
                </ContextMenuItem>
                {!is.undefined(onComment) && (
                    <ContextMenuItem onSelect={() => onComment(rowRef.current)}>
                        <IconChatBubble7 size={12} />
                        {translate('pages.job-architecture-v2.comments.selection-button.label')}
                    </ContextMenuItem>
                )}
                <ContextMenuItem
                    onSelect={() => {
                        void handleCopy();
                    }}
                >
                    <IconCopy size={12} />
                    {translate('common.copy')}
                </ContextMenuItem>
            </ContextMenuContent>
        </ContextMenu>
    );
};

const CommentableSkillRow: FunctionComponent<
    PropsWithChildren<{
        readonly itemId: string;
        readonly newThreadCreationDisabled: boolean;
        readonly onOpenSkillDetails: () => void;
        readonly skillId: string;
        readonly skillTitle: string;
    }>
> = ({ children, itemId, newThreadCreationDisabled, onOpenSkillDetails, skillId, skillTitle }) => {
    const { attrs, state, commentId, openThread, startComment } = useCommentableAnchor({
        kind: 'skill',
        itemId,
        skillId,
    });

    return (
        <SkillRowContextMenu
            dataAttributes={attrs}
            highlightClassName={commentHighlightRowClassName(state)}
            skillId={skillId}
            skillTitle={skillTitle}
            onComment={newThreadCreationDisabled ? undefined : startComment}
            onOpenSkillDetails={onOpenSkillDetails}
            onSelectExistingComment={is.undefined(commentId) ? undefined : openThread}
        >
            {children}
        </SkillRowContextMenu>
    );
};

const SkillRowFrame: FunctionComponent<{
    readonly children: ReactNode;
    readonly commentable: boolean;
    readonly itemId: string | undefined;
    readonly newThreadCreationDisabled: boolean;
    readonly onOpenSkillDetails: () => void;
    readonly skillId: string;
    readonly skillTitle: string;
}> = ({ children, commentable, itemId, newThreadCreationDisabled, onOpenSkillDetails, skillId, skillTitle }) => {
    if (commentable && !is.undefined(itemId)) {
        return (
            <CommentableSkillRow
                itemId={itemId}
                newThreadCreationDisabled={newThreadCreationDisabled}
                skillId={skillId}
                skillTitle={skillTitle}
                onOpenSkillDetails={onOpenSkillDetails}
            >
                {children}
            </CommentableSkillRow>
        );
    }

    return (
        <SkillRowContextMenu skillId={skillId} skillTitle={skillTitle} onOpenSkillDetails={onOpenSkillDetails}>
            {children}
        </SkillRowContextMenu>
    );
};

export const ReadOnlySkillRow: FunctionComponent<{
    readonly level: SkillProficiencyLevel | null | undefined;
    readonly onOpenSkillDetails: () => void;
    readonly skill: {
        readonly id: string;
        readonly title: string;
        readonly type: 'hard' | 'language' | 'soft';
    };
}> = ({ level, onOpenSkillDetails, skill }) => {
    const { getProficiencyLevelName } = useSkillProficiencyLevelName();

    return (
        <SkillRowContextMenu skillId={skill.id} skillTitle={skill.title} onOpenSkillDetails={onOpenSkillDetails}>
            <SkillBadgeSlot>
                <SkillBadge
                    className="max-w-full"
                    skill={{ title: skill.title, type: skill.type }}
                    onClick={onOpenSkillDetails}
                />
            </SkillBadgeSlot>
            <Row>
                <ProficiencyLevel
                    data-testid="proficiency-level"
                    title={getProficiencyLevelName({ level, skillType: skill.type })}
                >
                    <ProficiencyScale active={true} level={level} scaleCount={5} />
                </ProficiencyLevel>
            </Row>
        </SkillRowContextMenu>
    );
};

const SkillChipWithProficiencyLabelImpl: FunctionComponent<{
    readonly commentable: boolean;
    readonly hasValidationError?: boolean;
    readonly isEditMode: boolean;
    readonly isSelected?: boolean;
    readonly itemId?: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly onClick: (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => void;
    readonly onRemoveSkill: (skillId: string) => void;
    readonly showProficiency?: boolean;
    readonly skill: JobProfileSkillWithDiff;
}> = ({
    commentable,
    hasValidationError,
    isEditMode,
    isSelected = false,
    itemId,
    newThreadCreationDisabled = false,
    onChangeProficiencyLevel,
    onClick,
    onRemoveSkill,
    showProficiency = true,
    skill,
}) => {
    const { getProficiencyLevelName } = useSkillProficiencyLevelName();
    const translate = useTranslate();
    const { activeLanguage } = useActiveLanguage();
    const proficiencyTriggerRef = useRef<HTMLButtonElement>(null);
    const [isProficiencyOpen, setIsProficiencyOpen] = useState(false);

    const scale = useMemo(() => {
        return <ProficiencyScale active={true} level={skill.level} scaleCount={5} />;
    }, [skill.level]);

    const handleTriggerProficiencyDropdown = (): void => {
        setIsProficiencyOpen(true);
        if (proficiencyTriggerRef.current !== null) {
            proficiencyTriggerRef.current.click();
        }
    };

    const skillTitle = skill.titles.find((title) => title.language === activeLanguage)?.title ?? skill.title;

    const badge = (
        <SkillBadge
            $isSelected={isSelected}
            className="max-w-full"
            skill={{
                title: skillTitle,
                type: skill.skillType,
            }}
            onClick={() => onClick(skill.id, skill.level)}
        />
    );

    return (
        <SkillRowFrame
            commentable={commentable}
            itemId={itemId}
            newThreadCreationDisabled={newThreadCreationDisabled}
            skillId={skill.id}
            skillTitle={skillTitle}
            onOpenSkillDetails={() => onClick(skill.id, skill.level)}
        >
            <SkillBadgeSlot>{badge}</SkillBadgeSlot>
            <SkillNotSyncedWarning isSynced={skill.isSynced} />
            {skill.diff !== undefined ? (
                <Badge variant={SKILL_DIFF_BADGE_VARIANT[skill.diff]}>
                    {translate(`components.skills.${skill.diff}`)}
                </Badge>
            ) : null}
            {(showProficiency || isEditMode) && (
                <Row>
                    {showProficiency && (
                        <DropdownMenu open={isProficiencyOpen} onOpenChange={setIsProficiencyOpen}>
                            <DropdownMenuTrigger asChild={true}>
                                <UnstyledButton ref={proficiencyTriggerRef}>
                                    <ProficiencyLevel
                                        data-hasError={hasValidationError}
                                        {...{ [ANCHOR_SKILL_ID_ATTRIBUTE]: skill.id }}
                                        data-testid="proficiency-level"
                                        hasError={hasValidationError}
                                        title={getProficiencyLevelName({
                                            level: skill.level,
                                            skillType: skill.skillType,
                                        })}
                                    >
                                        {scale}
                                    </ProficiencyLevel>
                                </UnstyledButton>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="center"
                                collisionPadding={16}
                                side="right"
                                sideOffset={8}
                                style={{ overflow: 'hidden', zIndex: 2000 }}
                            >
                                <ProficiencyLevelDropdownMenu
                                    activeLevel={skill.level}
                                    disabled={isEditMode === false}
                                    id={skill.id}
                                    onChangeProficiencyLevel={onChangeProficiencyLevel}
                                />
                            </DropdownMenuContent>
                        </DropdownMenu>
                    )}
                    {isEditMode && (
                        <SkillContextMenu
                            skillTitle={skillTitle}
                            onDelete={() => onRemoveSkill(skill.id)}
                            onShowSkillDetails={() => onClick(skill.id, skill.level)}
                            onTriggerProficiencyDropdown={
                                showProficiency ? handleTriggerProficiencyDropdown : undefined
                            }
                        />
                    )}
                </Row>
            )}
        </SkillRowFrame>
    );
};

export const SkillChipWithProficiencyLabel = memo(SkillChipWithProficiencyLabelImpl);

export const SkillList: FunctionComponent<{
    readonly commentable: boolean;
    readonly formErrors?: FieldErrors<ProfileForm>;
    readonly highlightSelectedSkill?: boolean;
    readonly isInEditMode: boolean;
    readonly itemId?: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly onRemoveSkill: (skillId: string) => void;
    readonly onSkillChange?: () => void;
    readonly openSkillDetails: (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => void;
    readonly showProficiency: boolean;
    readonly skills: readonly JobProfileSkill[];
}> = ({
    commentable,
    formErrors,
    highlightSelectedSkill = false,
    isInEditMode,
    itemId,
    newThreadCreationDisabled = false,
    onChangeProficiencyLevel,
    onRemoveSkill,
    onSkillChange,
    openSkillDetails,
    showProficiency,
    skills,
}) => {
    const hasValidationError = formErrors?.competencies !== undefined;
    const isSkillSelected = useOpenTileSelection({
        selectId: selectSkillIdFromTile,
        slotKey: SKILL_DETAIL_TILE_SLOT_KEY,
    });

    const handleChangeProficiencyLevel = useCallback(
        (skillId: string, level: JobProfileSkill['level']): void => {
            onSkillChange?.();
            onChangeProficiencyLevel(skillId, level);
        },
        [onSkillChange, onChangeProficiencyLevel],
    );

    const handleRemoveSkill = useCallback(
        (skillId: string): void => {
            onSkillChange?.();
            onRemoveSkill(skillId);
        },
        [onSkillChange, onRemoveSkill],
    );

    return (
        <Fragment>
            {skills.map((skill, skillIndex) => {
                const skillHasError = hasValidationError && typeof skill.level !== 'number';

                const chip = (
                    <SkillChipWithProficiencyLabel
                        key={skill.id}
                        commentable={commentable}
                        hasValidationError={skillHasError}
                        isEditMode={isInEditMode}
                        isSelected={highlightSelectedSkill && isSkillSelected(skill.id)}
                        itemId={itemId}
                        newThreadCreationDisabled={newThreadCreationDisabled}
                        showProficiency={showProficiency}
                        skill={skill}
                        onChangeProficiencyLevel={handleChangeProficiencyLevel}
                        onClick={openSkillDetails}
                        onRemoveSkill={handleRemoveSkill}
                    />
                );

                if (!isInEditMode) {
                    return (
                        <DragWrapper key={skill.id} isDragEnabled={false}>
                            {chip}
                        </DragWrapper>
                    );
                }

                return (
                    <Draggable
                        key={skill.id}
                        draggableId={skill.id}
                        index={skillIndex}
                        isDragDisabled={skill.skillType === 'language'}
                    >
                        {({ innerRef, dragHandleProps, draggableProps: { style, ...draggableProps } }, snapshot) => {
                            if (snapshot.isDragging) {
                                return ReactDOM.createPortal(
                                    <DragWrapper
                                        ref={innerRef}
                                        isDragEnabled={isInEditMode}
                                        {...draggableProps}
                                        {...dragHandleProps}
                                        style={{ ...style }}
                                    >
                                        {chip}
                                    </DragWrapper>,
                                    document.body,
                                );
                            }

                            return (
                                <DragWrapper
                                    ref={innerRef}
                                    isDragEnabled={isInEditMode}
                                    {...draggableProps}
                                    {...dragHandleProps}
                                    style={{ ...style }}
                                >
                                    {chip}
                                </DragWrapper>
                            );
                        }}
                    </Draggable>
                );
            })}
        </Fragment>
    );
};
